/**
 * MultiSelectChips — выбор N значений из справочника, показан как чипы.
 *
 * Использование: «Цели выращивания» в редакторе культуры, потенциально
 * любое M:N поле справочника в reference-pattern.
 *
 * Для простоты v1 — все доступные опции в popover-списке, фильтр поиском.
 * Если у tenant'а будет 200+ goals, переделаем на async-search; пока 7 целей —
 * хватит.
 */
import { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectChipsProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  emptyHint?: string;
  disabled?: boolean;
}

export function MultiSelectChips({
  options,
  selected,
  onChange,
  placeholder = 'Выберите значения…',
  emptyHint = 'Ничего не найдено',
  disabled,
}: MultiSelectChipsProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const labelOf = (v: string) =>
    options.find((o) => o.value === v)?.label ?? v;

  const toggle = (v: string) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };

  const remove = (v: string) => onChange(selected.filter((x) => x !== v));

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 text-left text-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          {selected.length === 0 && (
            <span className="px-1 text-muted-foreground">{placeholder}</span>
          )}
          {selected.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
            >
              {labelOf(v)}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(v);
                }}
                className="rounded p-0.5 hover:bg-primary/20"
                aria-label={`Убрать ${labelOf(v)}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="border-b border-border p-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск…"
            className="h-8"
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              {emptyHint}
            </div>
          ) : (
            filtered.map((o) => {
              const isSel = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
                >
                  <span
                    className={cn(
                      'inline-flex h-4 w-4 items-center justify-center rounded border',
                      isSel
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input',
                    )}
                  >
                    {isSel && <Check className="h-3 w-3" />}
                  </span>
                  {o.label}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
